import type { SkinAssetMetadata } from './asset';
import type {
  SkinLodLevel,
  SkinModality,
  SkinModalityProfile,
  SkinParticleTexture,
  SkinSignature,
  SkinVfxDescriptor,
} from './vfx';

/**
 * The descriptor, turned into a bounded plan a renderer can execute
 * (ADR 0041).
 *
 * ## Why this layer exists
 *
 * So that no scene ever contains `switch (skinId)`. A renderer that knew which
 * skin it was drawing would grow one branch per skin for ever, and every one of
 * those branches would be a place a gameplay rule could be smuggled in. Instead
 * the scene consumes a plan: a fixed list of layers, each with a texture, a
 * ceiling, a depth and an offset, and nothing that names a particular skin.
 *
 * ## Pure, and provably so
 *
 * No Phaser, no canvas, no DOM, no clock, no randomness. The same descriptor and
 * the same modality always produce the same plan, field for field - which is
 * what lets the plan be compared in a test instead of a screenshot.
 *
 * ## What is deliberately NOT here
 *
 * Any gameplay quantity. There is no damage, no health, no speed, no hitbox, no
 * targeting and no currency in this file or in the type it produces, and there
 * is no field one could be written into. A skin decides what a ship looks like.
 */

/**
 * Where a skin's layers sit, relative to the depths the arena already uses.
 *
 * `PvpScene`'s table is the authority: ship 30, cannon 31, identity 35, rune 36,
 * bar 40, projectile 20, projectile wake 18. The two numbers below are chosen
 * against it and are stated as fractions on purpose - inserting a whole step
 * would have meant renumbering an ordering that four other things depend on.
 *
 * - The overlay and every signature sit at 30.5: ABOVE the hull they decorate,
 *   BELOW the cannon, and therefore below the team mark, the rune indicator and
 *   the health bar as well. A skin can never cover a signal the player needs.
 * - The motion trail sits at 16: below the projectile wake at 18, which is
 *   itself below the projectiles. Residue left by a ship must never be mistaken
 *   for residue left by a shot, and neither may be mistaken for a shot.
 */
export const SKIN_DEPTH = Object.freeze({
  trail: 16,
  overlay: 30.5,
  signature: 30.5,
});

/** One thing the renderer has to create, sized and bounded. */
export interface SkinLayerPlan {
  /** Stable within a plan, so a pool can be keyed on it. */
  readonly id: string;
  readonly role: 'overlay' | 'trail' | 'signature';
  /** For a signature, which one. `null` for the two mandatory layers. */
  readonly signatureKind: SkinSignature['kind'] | null;
  readonly texture: SkinParticleTexture | null;
  readonly colors: readonly string[];
  readonly depth: number;
  readonly blend: 'normal' | 'add';
  /** Emissions per second AFTER the modality and LOD scales. */
  readonly frequencyHz: number;
  /** Milliseconds, after the scales. */
  readonly lifespanMs: { readonly min: number; readonly max: number };
  /** Alpha is never scaled: a faint effect that got fainter would vanish. */
  readonly alpha: { readonly min: number; readonly max: number };
  /** As a fraction of the hull's drawn diameter. */
  readonly scale: { readonly min: number; readonly max: number };
  /** Hull-local, in hull diameters. */
  readonly offset: { readonly forward: number; readonly lateral: number };
  /**
   * How far from that offset a particle may be born, in hull diameters.
   *
   * The overlay declares its own and it used to be dropped here, so the
   * renderer scattered every skin's body sparkle over half a hull whatever the
   * descriptor asked for - a bounded field that existed and did nothing.
   *
   * The trail and the particle signatures have no such field, and inventing one
   * would be a format change for a knob nobody has asked to author. They take
   * their grain from their own largest particle instead: a band made of
   * quarter-hull motes is a quarter-hull wide, which is the only width that
   * reads as grain rather than as a line.
   */
  readonly spread: number;
  /** The hard ceiling on live particles for this layer, after the scales. */
  readonly maxParticles: number;
  /** Only a trail has one. Hull diameters. */
  readonly maxLengthHulls: number | null;
  /** Only a trail has one. Hull diameters per second. */
  readonly minSpeedHullsPerSecond: number | null;
  /** Only a trail has one. */
  readonly onDiscontinuity: 'clear' | 'hold' | null;
  /** Only a magic circle has these. */
  readonly circle: {
    readonly diameter: number;
    readonly rotationsPerSecond: number;
    readonly count: number;
  } | null;
}

export interface SkinVisualPlan {
  readonly definitionId: string;
  readonly modality: SkinModality;
  readonly lod: SkinLodLevel;
  /** The hull PNG this plan draws instead of the ordinary ship texture. */
  readonly assetKey: string;
  readonly assetFile: string;
  /** Normalised, from the asset metadata. The renderer sets the sprite origin. */
  readonly pivot: { readonly x: number; readonly y: number };
  readonly hullCenter: { readonly x: number; readonly y: number };
  readonly baseScale: number;
  /**
   * Where body sparkle may be born, as fractions of this skin's own texture.
   *
   * Straight from the sidecar the pipeline wrote, which read them once out of
   * the master's alpha with an erosion applied. The renderer transforms them by
   * the same placement the hull sprite gets - see `skinTexturePointToWorld` -
   * so a particle lands on paint at every rotation and every visual scale.
   *
   * Empty means "no body sparkle", which is legal: a descriptor with no overlay
   * has nowhere to put one.
   */
  readonly bodyPoints: readonly { readonly x: number; readonly y: number }[];
  /*
   * There is deliberately no `cannonHardpoint` here (gate V23.1C).
   *
   * The descriptor still declares one - it is honest art metadata saying where
   * the artist painted barrels - but it stops at authoring. It used to be
   * compiled into this plan and handed to the renderer, where nothing read it:
   * the last hook left over from the cannon struts V22D.1 deleted. A field
   * named `cannonHardpoint`, sitting unused in the object a renderer holds, is
   * an invitation to place a cannon from skin art one day, which is the single
   * thing every gate since has forbidden. Absence is the guard.
   */
  /** Every texture the plan needs loaded, sorted and deduplicated. */
  readonly requiredTextures: readonly SkinParticleTexture[];
  readonly layers: readonly SkinLayerPlan[];
  /** The sum of every layer's ceiling: what this ship can cost, at worst. */
  readonly totalParticleCeiling: number;
  /**
   * Everything the renderer must release when the skin goes away.
   *
   * Named rather than implied, so "the scene tears down what it built" is a
   * list a test can compare instead of a habit somebody has to keep.
   */
  readonly cleanup: readonly string[];
}

/** Applies a scale and keeps the result a positive number the schema allows. */
function scaled(value: number, factor: number): number {
  // Rounded to six decimals: two plans built from the same inputs must be
  // byte-identical, and floating point multiplication is not associative.
  return Math.round(value * factor * 1e6) / 1e6;
}

function ceilingOf(value: number, factor: number): number {
  // At least one. A layer scaled to zero particles would silently disappear,
  // which is how a 3x3 would stop looking like the skin somebody bought.
  return Math.max(1, Math.floor(value * factor));
}

/**
 * The plan for one skin, in one modality, at one level of detail.
 *
 * The modality profile and the LOD compose: the profile says what this MODE
 * draws, the LOD says how much of it the current budget affords. Both may only
 * reduce, so the descriptor's own ceilings bound the result whatever they are.
 */
export function compileSkinVisualPlan(input: {
  readonly asset: SkinAssetMetadata;
  readonly vfx: SkinVfxDescriptor;
  readonly modality: SkinModality;
  readonly lod: SkinLodLevel;
  /** From the runtime sidecar. Absent in a caller that has no manifest. */
  readonly bodyPoints?: readonly { readonly x: number; readonly y: number }[];
}): SkinVisualPlan {
  const { asset, vfx, modality, lod } = input;
  const profile: SkinModalityProfile = vfx[modality];
  const detail: SkinModalityProfile = vfx.lod[lod];

  const frequency = profile.frequencyScale * detail.frequencyScale;
  const lifespan = profile.lifespanScale * detail.lifespanScale;
  const particles = profile.particleScale * detail.particleScale;

  const layers: SkinLayerPlan[] = [
    {
      id: 'overlay',
      role: 'overlay',
      signatureKind: null,
      texture: vfx.overlay.texture,
      colors: [...vfx.overlay.colors],
      depth: SKIN_DEPTH.overlay,
      blend: vfx.overlay.blend,
      frequencyHz: scaled(vfx.overlay.frequencyHz, frequency),
      lifespanMs: {
        min: scaled(vfx.overlay.lifespan.min, lifespan),
        max: scaled(vfx.overlay.lifespan.max, lifespan),
      },
      alpha: { min: vfx.overlay.alpha.min, max: vfx.overlay.alpha.max },
      scale: { min: vfx.overlay.scale.min, max: vfx.overlay.scale.max },
      offset: { forward: vfx.overlay.offset.forward, lateral: vfx.overlay.offset.lateral },
      spread: vfx.overlay.spread,
      maxParticles: ceilingOf(vfx.overlay.maxParticles, particles),
      maxLengthHulls: null,
      minSpeedHullsPerSecond: null,
      onDiscontinuity: null,
      circle: null,
    },
    {
      id: 'trail',
      role: 'trail',
      signatureKind: null,
      texture: vfx.trail.texture,
      colors: [...vfx.trail.colors],
      depth: SKIN_DEPTH.trail,
      blend: vfx.trail.blend,
      frequencyHz: scaled(vfx.trail.frequencyHz, frequency),
      lifespanMs: {
        min: scaled(vfx.trail.lifespan.min, lifespan),
        max: scaled(vfx.trail.lifespan.max, lifespan),
      },
      alpha: { min: vfx.trail.alpha.min, max: vfx.trail.alpha.max },
      scale: { min: vfx.trail.scale.min, max: vfx.trail.scale.max },
      offset: { forward: vfx.trail.offset.forward, lateral: vfx.trail.offset.lateral },
      spread: vfx.trail.scale.max,
      maxParticles: ceilingOf(vfx.trail.maxParticles, particles),
      maxLengthHulls: vfx.trail.maxLengthHulls,
      minSpeedHullsPerSecond: vfx.trail.minSpeedHullsPerSecond,
      onDiscontinuity: vfx.trail.onDiscontinuity,
      circle: null,
    },
  ];

  /*
   * The signatures this MODE draws, in the descriptor's own declaration order.
   *
   * Filtering by the profile rather than by the array's order is what makes the
   * plan independent of how the modality happened to list them - two profiles
   * naming the same kinds in different orders compile to the same plan.
   */
  const wanted = new Set(profile.signatures);
  for (const signature of vfx.signatures) {
    if (!wanted.has(signature.kind)) {
      continue;
    }
    if (signature.kind === 'small_magic_circle') {
      layers.push({
        id: `signature:${signature.kind}`,
        role: 'signature',
        signatureKind: signature.kind,
        texture: null,
        colors: [...signature.colors],
        depth: SKIN_DEPTH.signature,
        blend: 'add',
        // A circle is drawn, not emitted: it has no cadence and no lifespan.
        frequencyHz: 0,
        lifespanMs: { min: 0, max: 0 },
        alpha: { min: signature.alpha.min, max: signature.alpha.max },
        scale: { min: signature.diameter, max: signature.diameter },
        offset: { forward: signature.center.forward, lateral: signature.center.lateral },
        spread: 0,
        maxParticles: signature.count,
        maxLengthHulls: null,
        minSpeedHullsPerSecond: null,
        onDiscontinuity: null,
        circle: {
          diameter: signature.diameter,
          rotationsPerSecond: signature.rotationsPerSecond,
          count: signature.count,
        },
      });
      continue;
    }
    if (signature.kind === 'body_glyph') {
      layers.push({
        id: `signature:${signature.kind}`,
        role: 'signature',
        signatureKind: signature.kind,
        texture: signature.texture,
        colors: [...signature.colors],
        depth: SKIN_DEPTH.signature,
        blend: signature.blend,
        frequencyHz: 0,
        lifespanMs: { min: 0, max: 0 },
        alpha: { min: signature.alpha.min, max: signature.alpha.max },
        scale: { min: signature.scale.min, max: signature.scale.max },
        offset: { forward: signature.offset.forward, lateral: signature.offset.lateral },
        spread: 0,
        maxParticles: 1,
        maxLengthHulls: null,
        minSpeedHullsPerSecond: null,
        onDiscontinuity: null,
        circle: null,
      });
      continue;
    }
    layers.push({
      id: `signature:${signature.kind}`,
      role: 'signature',
      signatureKind: signature.kind,
      texture: signature.texture,
      colors: [...signature.colors],
      depth: SKIN_DEPTH.signature,
      blend: signature.blend,
      frequencyHz: scaled(signature.frequencyHz, frequency),
      lifespanMs: {
        min: scaled(signature.lifespan.min, lifespan),
        max: scaled(signature.lifespan.max, lifespan),
      },
      alpha: { min: signature.alpha.min, max: signature.alpha.max },
      scale: { min: signature.scale.min, max: signature.scale.max },
      offset: { forward: signature.offset.forward, lateral: signature.offset.lateral },
      spread: signature.scale.max,
      maxParticles: ceilingOf(signature.maxParticles, particles),
      maxLengthHulls: null,
      minSpeedHullsPerSecond: null,
      onDiscontinuity: null,
      circle: null,
    });
  }

  const textures = [
    ...new Set(
      layers
        .map((layer) => layer.texture)
        .filter((texture): texture is SkinParticleTexture => texture !== null),
    ),
  ].sort();

  return {
    definitionId: vfx.definitionId,
    modality,
    lod,
    assetKey: asset.assetKey,
    bodyPoints: input.bodyPoints ?? [],
    assetFile: asset.file,
    pivot: { x: asset.pivot.x, y: asset.pivot.y },
    hullCenter: { x: asset.hullCenter.x, y: asset.hullCenter.y },
    baseScale: asset.baseScale,
    requiredTextures: textures,
    layers,
    totalParticleCeiling: layers.reduce((total, layer) => total + layer.maxParticles, 0),
    cleanup: layers.map((layer) => layer.id).sort(),
  };
}

/**
 * The plan a renderer falls back to when a modality cannot be resolved.
 *
 * Deterministic, and it is still the skin: the descriptor's own `fallbackLod`
 * decides the level, the hunt profile decides the composition, and the two
 * mandatory layers are present whatever happens. A fallback that drew a bare
 * hull would look to a player exactly like the skin they paid for failing to
 * load.
 */
export function compileSkinFallbackPlan(input: {
  readonly asset: SkinAssetMetadata;
  readonly vfx: SkinVfxDescriptor;
}): SkinVisualPlan {
  return compileSkinVisualPlan({
    asset: input.asset,
    vfx: input.vfx,
    modality: 'hunt',
    lod: input.vfx.fallbackLod,
  });
}
